/**
 * Public site announcements provider – loads landing announcements and syncs dismissals across tabs.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from '@contexts/AuthContext';
import {
  SITE_ANNOUNCEMENTS_BROADCAST_CHANNEL,
  SITE_ANNOUNCEMENTS_CHANGED_EVENT,
  SITE_ANNOUNCEMENTS_POLL_INTERVAL_MS,
} from '@constants/siteAnnouncementsSync';
import type { SiteAnnouncement } from '@lambda/shared/types';
import { getPublicLandingAnnouncements } from '@services/publicLandingApi';
import {
  isLandingAnnouncementDismissed,
  setLandingAnnouncementDismissed,
} from '@utils/announcementDismissStorage';
import { siteAnnouncementsEqual } from '@utils/siteAnnouncementsEqual';
import {
  readPublicSiteAnnouncementsCache,
  writePublicSiteAnnouncementsCache,
} from '@utils/publicSiteAnnouncementsCache';
import type { SiteAnnouncementsBroadcastMessage } from '@utils/siteAnnouncementsSync';
import { PublicSiteAnnouncementsContext, type PublicSiteAnnouncementsValue } from './PublicSiteAnnouncementsContext';

function filterDismissed(list: SiteAnnouncement[]): SiteAnnouncement[] {
  return list.filter((a) => !isLandingAnnouncementDismissed(a.announcementId));
}

export function PublicSiteAnnouncementsProvider({ children }: { children: ReactNode }) {
  const { user, token } = useAuth();
  const isFullySignedIn = Boolean(token && user?.twoFactorEnabled);

  const cachedOnMount = readPublicSiteAnnouncementsCache();
  const [announcements, setAnnouncements] = useState<SiteAnnouncement[]>(
    cachedOnMount ? filterDismissed(cachedOnMount) : []
  );
  const [loading, setLoading] = useState(cachedOnMount === null && !isFullySignedIn);

  // Landing announcements are not loaded for fully signed-in users, so there is nothing to wait
  // for (adjust state during render).
  if (isFullySignedIn && loading) {
    setLoading(false);
  }

  /** Fetches landing announcements and applies them when the response arrives. */
  const fetchAnnouncements = useCallback(
    () =>
      getPublicLandingAnnouncements().then((list) => {
        const next = filterDismissed(list ?? []);
        writePublicSiteAnnouncementsCache(list ?? []);
        setAnnouncements((prev) => (siteAnnouncementsEqual(prev, next) ? prev : next));
        return next;
      }),
    []
  );

  const refetch = useCallback(async () => {
    await fetchAnnouncements();
  }, [fetchAnnouncements]);

  useEffect(() => {
    if (isFullySignedIn) return;
    let cancelled = false;
    void fetchAnnouncements().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [fetchAnnouncements, isFullySignedIn]);

  useEffect(() => {
    if (isFullySignedIn) return;
    const intervalId = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      void fetchAnnouncements();
    }, SITE_ANNOUNCEMENTS_POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [fetchAnnouncements, isFullySignedIn]);

  useEffect(() => {
    if (isFullySignedIn) return;
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void fetchAnnouncements();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [fetchAnnouncements, isFullySignedIn]);

  useEffect(() => {
    if (isFullySignedIn) return;
    const onChanged = () => {
      const cached = readPublicSiteAnnouncementsCache();
      if (cached) {
        const next = filterDismissed(cached);
        setAnnouncements((prev) => (siteAnnouncementsEqual(prev, next) ? prev : next));
      }
      void fetchAnnouncements();
    };
    window.addEventListener(SITE_ANNOUNCEMENTS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(SITE_ANNOUNCEMENTS_CHANGED_EVENT, onChanged);
  }, [fetchAnnouncements, isFullySignedIn]);

  useEffect(() => {
    if (isFullySignedIn) return;
    if (typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel(SITE_ANNOUNCEMENTS_BROADCAST_CHANNEL);
    channel.onmessage = (event: MessageEvent<SiteAnnouncementsBroadcastMessage>) => {
      if (event.data?.type === 'announcements-changed') {
        void fetchAnnouncements();
      }
    };
    return () => channel.close();
  }, [fetchAnnouncements, isFullySignedIn]);

  const dismiss = useCallback((announcementId: string) => {
    setLandingAnnouncementDismissed(announcementId);
    setAnnouncements((prev) => prev.filter((a) => a.announcementId !== announcementId));
  }, []);

  const value = useMemo(
    (): PublicSiteAnnouncementsValue => ({
      announcements,
      loading,
      dismiss,
      refetch,
    }),
    [announcements, loading, dismiss, refetch]
  );

  return (
    <PublicSiteAnnouncementsContext value={value}>
      {children}
    </PublicSiteAnnouncementsContext>
  );
}
